import React, { useState } from 'react';
import ReactModal from 'react-modal';
import { Loader } from 'semantic-ui-react';
import { useDispatch } from 'react-redux';
import { Permissions } from '@upstart/harmony/lib/constants';
import { MessageType } from '@upstart/harmony/lib/legos/websiteTypes';
import { useDealerActivation } from 'hooks/api/dealerActivation';
import { PageMessage } from 'components/GlobalUI';
import { dealersRequest } from '../../redux/dealers/actions';
import ProdigySelect from '../GlobalUI/ProdigySelect';
import withDealerPermission from './dealerPermission';
import '../ReactModal/reactModal.scss';

function ReactivateButton() {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDealerId, setSelectedDealerId] = useState<string | null>(null);
  const {
    deletedDealers,
    isLoadingDeletedDealers,
    reactivateDealer,
    isReactivating,
  } = useDealerActivation({ enabled: isOpen });

  const open = () => {
    setIsOpen(true);
    setSelectedDealerId(null);
  };

  const close = () => setIsOpen(false);

  const submit = async () => {
    if (!selectedDealerId) return;
    try {
      await reactivateDealer({ dealerId: selectedDealerId });
      dispatch(dealersRequest({ addExtraInformation: false }));
      close();
    } catch (error) {
      PageMessage({
        headline: 'Reactivation failed',
        message:
          error instanceof Error
            ? error.message
            : 'Could not reactivate the dealer. Please try again.',
        type: MessageType.ERROR,
      });
    }
  };

  const options = deletedDealers.map((dealer) => ({
    value: dealer.id,
    label: dealer.name ? `${dealer.name} (${dealer.id})` : dealer.id,
  }));

  return (
    <>
      <button
        type="button"
        className="btn btn-primary btn-large new-btn-space"
        onClick={open}
      >
        Reactivate
      </button>
      <ReactModal
        isOpen={isOpen}
        onRequestClose={close}
        ariaHideApp={false}
        className="p-modal reactivate-dealer-modal"
        overlayClassName="p-modal-container"
      >
        <i
          onClick={close}
          className="modal-close close icon"
          aria-label="close"
        />
        <h2>Reactivate Dealer</h2>
        {isLoadingDeletedDealers ? (
          <div className="reactivate-dealer-loading">
            <Loader
              active
              inline
              size="small"
              data-testid="deleted-dealers-loader"
            />
            <p>Loading deactivated dealers...</p>
          </div>
        ) : (
          <div className="reactivate-dealer-body">
            <ProdigySelect
              id="reactivate-dealer-select"
              placeholder="Select dealer..."
              options={options}
              value={selectedDealerId}
              height={34}
              onChange={(option: { value: string } | null) =>
                setSelectedDealerId(option?.value ?? null)
              }
            />
            <button
              type="button"
              className="btn btn-primary"
              disabled={!selectedDealerId || isReactivating}
              onClick={submit}
            >
              {isReactivating ? (
                <Loader
                  active
                  inverted
                  inline
                  size="tiny"
                  data-testid="reactivate-submit-loader"
                />
              ) : (
                'Submit'
              )}
            </button>
          </div>
        )}
      </ReactModal>
    </>
  );
}

export default withDealerPermission(Permissions.DELETE)(ReactivateButton);
